import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSpinner, FaCopy, FaCheck } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  createPacsShareLink,
  getPacsShareErrorMessage,
  searchPacsSeries,
  searchPacsStudies,
} from "../../api/pacsShareApi";

function formatDicomPersonName(name) {
  if (!name) return "—";
  const cleaned = String(name)
    .replace(/\^+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || "—";
}

function formatDicomDate(value) {
  if (!value) return "—";
  const digits = String(value).replace(/\D/g, "");
  if (digits.length >= 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  return String(value);
}

function formatExpiry(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function MetaCell({ label, value }) {
  return (
    <div className="min-w-0">
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
        {label}
      </span>
      <strong className="block text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
        {value || "—"}
      </strong>
    </div>
  );
}

export default function PacsSharePanel({
  organisationId,
  accessionNumber = "",
  onShareLinkReady,
  phoneNumber = "",
  onPhoneNumberChange,
  onConnectPhone,
  isConnectingPhone = false,
  phoneError = "",
  connectedNumber = "",
  onPhoneKeyDown,
  onChangeNumber,
  chatCard = null,
}) {
  const { t } = useTranslation("common");
  const [accInput, setAccInput] = useState(accessionNumber || "");
  const [studies, setStudies] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");
  const [seriesList, setSeriesList] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingSeries, setIsLoadingSeries] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [searchMeta, setSearchMeta] = useState("");

  useEffect(() => {
    setAccInput(accessionNumber || "");
    setStudies([]);
    setSelectedUid("");
    setSeriesList([]);
    setShareResult(null);
    setSearchMeta("");
    onShareLinkReady?.("");
  }, [accessionNumber, organisationId, onShareLinkReady]);

  const selectedStudy = studies.find((s) => s.studyInstanceUID === selectedUid);

  const loadSeries = useCallback(
    async (studyInstanceUID) => {
      if (!organisationId || !studyInstanceUID) {
        setSeriesList([]);
        return;
      }
      setIsLoadingSeries(true);
      try {
        const series = await searchPacsSeries({
          organisationId,
          studyInstanceUID,
        });
        setSeriesList(series);
      } catch (error) {
        setSeriesList([]);
        toast.error(
          getPacsShareErrorMessage(
            error,
            t("pacsShareSeriesFailed") || "Failed to load series",
          ),
        );
      } finally {
        setIsLoadingSeries(false);
      }
    },
    [organisationId, t],
  );

  const handleSearch = async () => {
    const acc = accInput.trim();
    if (!organisationId) {
      toast.error(
        t("pacsShareMissingOrg") ||
          "Select an organisation before searching PACS.",
      );
      return;
    }
    if (!acc) {
      toast.error(
        t("pacsShareMissingAccession") || "Accession number is required.",
      );
      return;
    }

    setIsSearching(true);
    setShareResult(null);
    onShareLinkReady?.("");
    setSeriesList([]);
    setSelectedUid("");
    try {
      const found = await searchPacsStudies({
        organisationId,
        accessionNumber: acc,
      });
      setStudies(found);
      if (!found.length) {
        setSearchMeta(
          t("pacsShareNoStudies") || "No studies found for this accession.",
        );
        return;
      }
      setSearchMeta(
        t("pacsShareFoundByAccession", { acc }) ||
          `Study found by Accession ${acc}`,
      );
      const firstUid = found[0].studyInstanceUID;
      setSelectedUid(firstUid);
      await loadSeries(firstUid);
    } catch (error) {
      setStudies([]);
      toast.error(
        getPacsShareErrorMessage(
          error,
          t("pacsShareSearchFailed") || "Failed to search PACS",
        ),
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectStudy = async (study) => {
    if (!study?.studyInstanceUID || study.studyInstanceUID === selectedUid) {
      return;
    }
    setSelectedUid(study.studyInstanceUID);
    setShareResult(null);
    onShareLinkReady?.("");
    await loadSeries(study.studyInstanceUID);
  };

  const handleShare = async () => {
    if (!organisationId || !selectedStudy?.studyInstanceUID) return;
    const acc =
      selectedStudy.accessionNumber?.trim() || accInput.trim();
    if (!acc) {
      toast.error(
        t("pacsShareMissingAccession") || "Accession number is required.",
      );
      return;
    }

    setIsSharing(true);
    setShareResult(null);
    try {
      const result = await createPacsShareLink({
        organisationId,
        accessionNumber: acc,
        studyInstanceUID: selectedStudy.studyInstanceUID,
      });
      if (!result.downloadUrl) {
        throw new Error(
          t("pacsShareLinkMissing") || "Share link was not returned.",
        );
      }
      setShareResult(result);
      onShareLinkReady?.(result.downloadUrl);
      toast.success(t("pacsShareLinkReady") || "Share link is ready.");
    } catch (error) {
      toast.error(
        getPacsShareErrorMessage(
          error,
          t("pacsShareLinkFailed") || "Failed to create share link",
        ),
      );
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopy = async () => {
    if (!shareResult?.downloadUrl) return;
    try {
      await navigator.clipboard.writeText(shareResult.downloadUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t("pacsShareCopyFailed") || "Could not copy the link.");
    }
  };

  return (
    <section className="mb-4 p-4 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-3xl shadow-sm dark:shadow-gray-900">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t("pacsShareTitle") || "PACSShare DICOM Study Sharing"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("pacsShareSubtitle") ||
              "Search PACS, confirm the study, then create a secure download link for WhatsApp."}
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-end mb-4">
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t("pacsShareAccLabel") || "Acc #"}
          </label>
          <input
            value={accInput}
            onChange={(e) => setAccInput(e.target.value)}
            className="w-full h-11 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B]"
          />
        </div>
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t("pacsSharePhoneLabel") || "WhatsApp #"}
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                value={connectedNumber || phoneNumber}
                onChange={(e) => onPhoneNumberChange?.(e.target.value)}
                onKeyDown={onPhoneKeyDown}
                placeholder={t("chatNumberPlaceholder") || "+1234567890"}
                disabled={isConnectingPhone || Boolean(connectedNumber)}
                readOnly={Boolean(connectedNumber)}
                className={`w-full h-11 rounded-lg border dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B] ${
                  connectedNumber
                    ? "bg-gray-50 dark:bg-gray-700 pr-20 pl-3"
                    : "bg-white dark:bg-gray-700 px-3"
                }`}
              />
              {connectedNumber && onChangeNumber ? (
                <button
                  type="button"
                  onClick={onChangeNumber}
                  className="absolute inset-y-0 right-1 my-1 px-2.5 rounded-md text-xs font-semibold text-[#166d8b] dark:text-[#7dd3fc] hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {t("chatChangeNumber") || "Change"}
                </button>
              ) : null}
            </div>
            {!connectedNumber ? (
              <button
                type="button"
                onClick={onConnectPhone}
                disabled={isConnectingPhone || !String(phoneNumber).trim()}
                className="h-11 px-4 rounded-lg bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {isConnectingPhone ? (
                  <>
                    <FaSpinner className="animate-spin" size={14} />
                    {t("loading") || "Connecting..."}
                  </>
                ) : (
                  t("chatConnect") || "Connect"
                )}
              </button>
            ) : null}
          </div>
          {phoneError ? (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{phoneError}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={isSearching || isSharing}
          className="h-11 px-4 rounded-lg bg-[#166d8b] hover:bg-[#0e4e66] text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          {isSearching ? (
            <>
              <FaSpinner className="animate-spin" size={14} />
              {t("pacsShareSearching") || "Searching..."}
            </>
          ) : (
            t("pacsShareSearch") || "Search Study in PACS"
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-4">
        <div className="relative min-h-[16rem] lg:min-h-0">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-full lg:absolute lg:inset-0">
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 shrink-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t("pacsShareResults") || "Search Results"}
            </h3>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {searchMeta ||
                (t("pacsShareResultsHint") ||
                  "Search by the active accession number")}
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto flex flex-col">
          {!studies.length ? (
            <div className="flex-1 flex items-center justify-center p-4 text-sm text-center text-gray-500 dark:text-gray-400">
              {t("pacsShareEmpty") ||
                "No study selected yet. Search PACS to review Study and Series details before sharing."}
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {selectedStudy && (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 rounded-xl border border-sky-100 dark:border-gray-600 bg-sky-50/60 dark:bg-gray-700/40 p-3">
                  <MetaCell
                    label={t("pacsSharePatientName") || "Patient Name"}
                    value={formatDicomPersonName(selectedStudy.patientName)}
                  />
                  <MetaCell
                    label={t("pacsSharePatientId") || "Patient ID"}
                    value={selectedStudy.patientID}
                  />
                  <MetaCell
                    label={t("pacsShareAccession") || "Accession"}
                    value={selectedStudy.accessionNumber}
                  />
                  <MetaCell
                    label={t("pacsShareStudyDate") || "Study Date"}
                    value={formatDicomDate(selectedStudy.studyDate)}
                  />
                  <MetaCell
                    label={t("pacsShareModality") || "Modality"}
                    value={selectedStudy.modality}
                  />
                </div>
              )}

              {studies.map((study) => {
                const isSelected = study.studyInstanceUID === selectedUid;
                return (
                  <div
                    key={study.studyInstanceUID || study.accessionNumber}
                    className={`rounded-xl border ${
                      isSelected
                        ? "border-[#166d8b] ring-1 ring-[#166d8b]/30"
                        : "border-gray-200 dark:border-gray-600"
                    } bg-white dark:bg-gray-800`}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelectStudy(study)}
                      className="w-full text-left p-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 dark:text-gray-100">
                            {formatDicomPersonName(study.patientName)}{" "}
                            <span className="font-medium text-gray-500 dark:text-gray-400">
                              ({study.patientID || "—"})
                            </span>
                          </div>
                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 dark:text-gray-300">
                            <span>
                              <strong>{t("pacsShareAccShort") || "Acc"}:</strong>{" "}
                              {study.accessionNumber || "—"}
                            </span>
                            <span>
                              <strong>
                                {t("pacsShareModality") || "Modality"}:
                              </strong>{" "}
                              {study.modality || "—"}
                            </span>
                            <span>
                              <strong>
                                {t("pacsShareStudyDate") || "Study Date"}:
                              </strong>{" "}
                              {formatDicomDate(study.studyDate)}
                            </span>
                            <span>
                              <strong>
                                {t("pacsShareSeriesCount") || "Series"}:
                              </strong>{" "}
                              {study.numberOfStudyRelatedSeries ?? "—"}
                            </span>
                          </div>
                          {study.studyDescription ? (
                            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              {study.studyDescription}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </button>
                    {isSelected && (
                      <div className="px-4 pb-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {t("pacsShareSeriesList") || "Series List"}
                          </div>
                          <button
                            type="button"
                            onClick={handleShare}
                            disabled={isSharing || isSearching}
                            className="px-4 py-2 rounded-lg bg-[#166d8b] hover:bg-[#0e4e66] text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                          >
                            {isSharing ? (
                              <>
                                <FaSpinner className="animate-spin" size={14} />
                                {t("pacsShareSharing") || "Sharing..."}
                              </>
                            ) : (
                              t("pacsShareLink") || "Share Link"
                            )}
                          </button>
                        </div>
                        {isLoadingSeries ? (
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-2">
                            <FaSpinner className="animate-spin" size={12} />
                            {t("loading") || "Loading..."}
                          </div>
                        ) : !seriesList.length ? (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {t("pacsShareNoSeries") || "No series returned."}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {seriesList.map((series) => (
                              <div
                                key={
                                  series.seriesInstanceUID ||
                                  `${series.seriesNumber}-${series.modality}`
                                }
                                className="grid grid-cols-[48px_1fr_auto_auto] gap-3 items-center rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 px-3 py-2 text-sm"
                              >
                                <span className="font-bold text-gray-900 dark:text-gray-100">
                                  #{series.seriesNumber || "—"}
                                </span>
                                <span className="text-gray-600 dark:text-gray-300 truncate">
                                  {series.seriesDescription ||
                                    t("pacsShareNoDescription") ||
                                    "No description"}
                                </span>
                                <span className="font-semibold text-gray-800 dark:text-gray-200">
                                  {series.modality || ""}
                                </span>
                                <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                  {t("pacsShareImages") || "Images"}:{" "}
                                  {series.numberOfSeriesRelatedInstances ?? "—"}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t("pacsShareProgressTitle") || "Download Progress to Cloud"}
              </h3>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between gap-2 text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                <span>
                  {isSharing
                    ? t("pacsShareRetrieving") ||
                      "Retrieving study from PACS. This can take up to 90 seconds."
                    : shareResult
                      ? t("pacsShareReady") || "Ready for secure sharing"
                      : t("pacsShareWaiting") || "Study is not shared yet"}
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                {isSharing ? (
                  <div className="h-full w-1/3 rounded-full bg-[#35c56b] animate-pulse" />
                ) : shareResult ? (
                  <div className="h-full w-full rounded-full bg-[#35c56b]" />
                ) : (
                  <div className="h-full w-0" />
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {isSharing
                  ? t("pacsShareRetrievingNote") ||
                    "Please wait. Do not retry if this takes a while — a retry starts a new PACS retrieve."
                  : t("pacsShareProgressNote") ||
                    "Click Share Link to retrieve the confirmed study into secure cloud storage."}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t("pacsShareLinkTitle") || "Link Created for Sharing"}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                <input
                  readOnly
                  value={shareResult?.downloadUrl || ""}
                  placeholder={
                    t("pacsShareLinkPlaceholder") ||
                    "Secure download link will appear here after the study is packaged"
                  }
                  className="flex-1 min-w-0 h-10 rounded-lg border dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 text-xs"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  disabled={!shareResult?.downloadUrl}
                  className="h-10 px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 disabled:opacity-50 inline-flex items-center gap-1.5 text-sm"
                >
                  {copied ? <FaCheck size={12} /> : <FaCopy size={12} />}
                  {copied
                    ? t("pacsShareCopied") || "Copied"
                    : t("pacsShareCopy") || "Copy"}
                </button>
              </div>
              {shareResult?.expiresAtUtc ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t("pacsShareExpires") || "Expires"}:{" "}
                  {formatExpiry(shareResult.expiresAtUtc)}
                </p>
              ) : null}
            </div>
          </div>
          {chatCard}
        </div>
      </div>
    </section>
  );
}
